import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames/bind";
import useWhoAmIContext from "common/providers/whoAmI/useWhoAmIContext";
import { comingSoonVehicleAlertModal } from "features/vehicleDetails/modals";
import { EventNames, usePub } from "hooks/usePublishSubscribe";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { ResolvedPDPPage, ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";
import { scrollIntoView } from "seamless-scroll-polyfill";
import { VehicleService } from "services/vehicle/vehicle";
import slugify from "slugify";
import { v4 } from "uuid";

import { PDPPromoArea } from "../PDPPromoArea";
import styles from "./VehicleOverview.module.scss";
import BuyPartExCtas, { BuyPartExCtasProps } from "./components/BuyPartExCtas";
import ContactSummary, { ContactSummaryProps } from "./components/ContactSummary";
import IncludedExtrasSummary, {
    IncludedExtrasSummaryProps,
} from "./components/IncludedExtrasSummary";
import NameTestDriveSummary, { NameTestDriveSummaryProps } from "./components/NameTestDriveSummary";
import PriceFinanceSummary, { PriceFinanceSummaryProps } from "./components/PriceFinanceSummary";
import StoreWarrentySummary, { StoreWarrantySummaryProps } from "./components/StoreWarrantySummary";
import VehicleImage from "./components/VehicleImage";
import VehicleImageCarousel, { VehicleImageCarouselProps } from "./components/VehicleImageCarousel";
import { useComingSoonAlert } from "./context";

const cx = classNames.bind(styles);

export type PriceFinanceSummaryContentProps = Omit<
    PriceFinanceSummaryProps,
    "handleCtaLeftClick" | "handleCtaRightClick"
>;

export type VehicleOverviewProps = {
    vehicle: VehicleService.Vehicle;
    showPromo: boolean;
    formattedMake: string;
    formattedModel: string;
    formattedVariant: string;
    isSaved?: boolean;
    vehicleCardSettings: ResolvedVehicleCardSettings;
    components: {
        buyPartExCtasProps: Omit<
            BuyPartExCtasProps,
            | "handleCtaLeftClick"
            | "handleCtaRightClick"
            | "formattedMake"
            | "formattedModel"
            | "formattedTrim"
            | "formattedVariant"
            | "stockBookNumber"
        >;
        contactSummaryProps: ContactSummaryProps;
        includedExtrasSummaryProps: Omit<IncludedExtrasSummaryProps, "handleButtonClick">;
        nameTestDriveSummaryProps: Omit<NameTestDriveSummaryProps, "handleButtonClick">;
        priceFinanceSummaryProps: Omit<
            PriceFinanceSummaryProps,
            "handleCtaLeftClick" | "handleCtaRightClick"
        >;
        storeWarrentySummaryProps: StoreWarrantySummaryProps;
        vehicleImageCarouselProps: Omit<
            VehicleImageCarouselProps,
            | "setCurrentSelectedImageIndex"
            | "currentSelectedImageIndex"
            | "handleImageViewerVideoOpen"
            | "handleImageViewerImageOpen"
            | "swiperRef"
        >;
        resolvedVehicleDisplaySettings: ResolvedVehicleDisplaySettings;
    };
    sideBarContent: ResolvedPDPPage["widgetRowContent"]["alertSidePanel"];
};

const VehicleOverview = ({
    vehicle,
    components,
    formattedMake,
    formattedModel,
    formattedVariant,
    googlePlaceId,
    isSaved,
    vehicleCardSettings,
    sideBarContent,
}: VehicleOverviewProps & { googlePlaceId?: string }) => {
    const { alert, setAlert } = useComingSoonAlert();
    const vehicleOverviewRef = useRef<HTMLDivElement | null>(null);
    const vehicleImageRef = useRef<HTMLDivElement | null>(null);
    const thumbsSwiperRef = useRef<any>(null);
    const { isLoggedIn } = useWhoAmIContext();
    const publish = usePub();

    const page = isLoggedIn ? "personal-details" : "create-account";
    const id = vehicle.LegacyVehicleId ?? vehicle.StockBookNumber;
    const variant = slugify(formattedVariant, { lower: true });
    const make = slugify(formattedMake, { lower: true });
    const model = slugify(formattedModel, { lower: true });
    const reserveBuyUrl = `/used-cars/${make}/${model}/${variant}/${id}/reserve/${v4()}/${page}`;
    const hasImagery = vehicle.Imagery.MarketingImages.length > 0;

    const [currentSelectedImageIndex, setCurrentSelectedImageIndex] = useState(0);

    const handleBuildFinanceClick = () => {
        const fc = document.getElementById("finance-deal-widget");

        if (fc) {
            scrollIntoView(fc, { block: "center", behavior: "smooth" });
        }
    };

    const processImageArrayToIncludeImageSizes = (vehicleImage: VehicleService.MarketingImage) => {
        vehicleImage.SizedImages?.sort((a, b) => b.Format.localeCompare(a.Format));
    };

    components.vehicleImageCarouselProps.media.MarketingImages.forEach((image) => {
        processImageArrayToIncludeImageSizes(image);
    });

    const handleImageViewerVideoOpen = () => {
        publish(EventNames.UpdateImageViewerMode, { modeValue: "360" });
        publish(EventNames.SetImageViewerOpenWithCompletionEvent, { isOpen: true });
    };

    const handleImageViewerImageOpen = (activeIndex: number) => {
        publish(EventNames.UpdateImageViewerActiveIndex, { index: activeIndex });
        publish(EventNames.UpdateImageViewerMode, { modeValue: "Gallery" });
        publish(EventNames.SetImageViewerOpenWithCompletionEvent, { isOpen: true });
    };

    useEffect(() => {
        if (!alert)
            setAlert({
                make: formattedMake,
                model: formattedModel,
                colour: vehicle.Colour.Name,
                stockNo: vehicle.StockBookNumber.toString(),
            });
    });

    return (
        <div className={cx("vehicleOverview", !hasImagery && "comingSoon")}>
            <div className={cx("vehicleOverviewImage")}>
                <VehicleImage
                    ref={vehicleImageRef}
                    thumbsSwiperRef={thumbsSwiperRef}
                    vehicle={vehicle}
                    currentSelectedImageIndex={currentSelectedImageIndex}
                    handleImageViewerImageOpen={handleImageViewerImageOpen}
                    setCurrentSelectedImageIndex={setCurrentSelectedImageIndex}
                    googlePlaceId={googlePlaceId}
                    isSaved={isSaved}
                    vehicleCardSettings={vehicleCardSettings}
                />
                {!!components?.vehicleImageCarouselProps?.showPromo && (
                    <PDPPromoArea>Promo 1</PDPPromoArea>
                )}
                {!vehicle.ComingSoonDetails?.IsComingSoon && (
                    <VehicleImageCarousel
                        setCurrentSelectedImageIndex={setCurrentSelectedImageIndex}
                        currentSelectedImageIndex={currentSelectedImageIndex}
                        handleImageViewerVideoOpen={handleImageViewerVideoOpen}
                        handleImageViewerImageOpen={handleImageViewerImageOpen}
                        {...components.vehicleImageCarouselProps}
                        swiperRef={thumbsSwiperRef}
                    />
                )}
            </div>
            <div
                ref={vehicleOverviewRef}
                id="vehicle-overview-details"
                className={cx("vehicleOverviewDetails")}
            >
                <StoreWarrentySummary
                    {...components.storeWarrentySummaryProps}
                    isComingSoon={vehicle.ComingSoonDetails?.IsComingSoon}
                />
                <NameTestDriveSummary
                    {...components.nameTestDriveSummaryProps}
                    isComingSoon={vehicle.ComingSoonDetails?.IsComingSoon}
                />
                <IncludedExtrasSummary
                    {...components.includedExtrasSummaryProps}
                    reserveUrl={reserveBuyUrl}
                    isComingSoon={vehicle.ComingSoonDetails?.IsComingSoon}
                />
                <div className={cx("vehicleOverviewPriceCtas")}>
                    <div className={cx("vehicleOverviewPrice")}>
                        <PriceFinanceSummary
                            {...components.priceFinanceSummaryProps}
                            handleCtaRightClick={handleBuildFinanceClick}
                        />
                    </div>
                    <div className={cx("vehicleOverviewCtas")}>
                        <BuyPartExCtas
                            {...components.buyPartExCtasProps}
                            isDiscounted={!!components.priceFinanceSummaryProps?.discount}
                            reserveUrl={reserveBuyUrl}
                            ctaComingSoonText={sideBarContent.alertCtaText}
                            isComingSoon={vehicle.ComingSoonDetails?.IsComingSoon}
                            openSideBarCallback={() =>
                                comingSoonVehicleAlertModal(vehicle, sideBarContent)
                            }
                        />
                    </div>
                </div>
                <ContactSummary
                    {...components.contactSummaryProps}
                    isComingSoon={vehicle.ComingSoonDetails?.IsComingSoon}
                />
            </div>
        </div>
    );
};

export default VehicleOverview;
